import {
    authenticatedApi,
    ApiError,
    API_BASE_URL
} from './apiUtils';

export const feedbackApi = {
  askForFeedback: async (data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/feedback/ask`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid feedback request', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Employee not found', 404);
      }
      throw new ApiError('Failed to request feedback', error.status || 500);
    }
  },

  // ADR-013: Feedback Session Modification Audit Report.
  // Cursor-paginated audit trail over the feedback_session_modification_audit table.
  getFeedbackModificationAuditReport: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.feedbackSessionId) { queryParams.append('feedbackSessionId', params.feedbackSessionId); }
      if (params.actorEmployeeId) { queryParams.append('actorEmployeeId', params.actorEmployeeId); }
      if (params.action) { queryParams.append('action', params.action); }
      if (params.destructiveOnly) { queryParams.append('destructiveOnly', 'true'); }
      if (params.employeeId) { queryParams.append('employeeId', params.employeeId); }
      if (params.dateFrom) { queryParams.append('dateFrom', params.dateFrom); }
      if (params.dateTo) { queryParams.append('dateTo', params.dateTo); }
      // Session-attribute filters.
      if (params.businessUnitId) { queryParams.append('businessUnitId', params.businessUnitId); }
      if (params.reviewType && params.reviewType.length) {
        queryParams.append('reviewType', Array.isArray(params.reviewType) ? params.reviewType.join(',') : params.reviewType);
      }
      if (params.month) { queryParams.append('month', params.month); }
      if (params.year) { queryParams.append('year', params.year); }
      if (params.sessionName) { queryParams.append('sessionName', params.sessionName); }
      if (params.projectId) { queryParams.append('projectId', params.projectId); }
      if (params.limit) { queryParams.append('limit', params.limit); }
      if (params.cursor) { queryParams.append('cursor', params.cursor); }
      if (params.orderDirection) { queryParams.append('orderDirection', params.orderDirection); }
      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/reports/feedback-session-modifications${queryString ? `?${queryString}` : ''}`;
      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch feedback modification audit report', error.status || 500);
    }
  },

  getSessions: async (options = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (options.light) queryParams.append('light', 'true');
      
      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/feedback-sessions${queryString ? `?${queryString}` : ''}`;
      
      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch feedback sessions', error.status || 500);
    }
  },

  getMySessionById: async (sessionId) => {
    try {
      const url = `${API_BASE_URL}/feedback-sessions/${sessionId}/my`;
      return await authenticatedApi.get(url);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError(error.data?.message || 'You are not authorized to access this feedback session', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'Feedback session not found', 404);
      }
      throw new ApiError('Failed to fetch feedback session details', error.status || 500);
    }
  },

  getSessionById: async (sessionId) => {
    try {
      const url = `${API_BASE_URL}/feedback-sessions/${sessionId}`;
      return await authenticatedApi.get(url);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError(error.data?.message || 'You are not authorized to access this feedback session', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'Feedback session not found', 404);
      }
      throw new ApiError('Failed to fetch feedback session details', error.status || 500);
    }
  },

  getIndividualAssignmentById: async (assignmentId) => {
    try {
      const url = `${API_BASE_URL}/individual-assignments/${assignmentId}`;
      return await authenticatedApi.get(url);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError(error.data?.message || 'You are not authorized to access this individual assignment', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'Individual assignment not found', 404);
      }
      throw new ApiError('Failed to fetch individual assignment details', error.status || 500);
    }
  },

  deleteIndividualAssignment: async (assignmentId) => {
    try {
      const url = `${API_BASE_URL}/individual-assignments/${assignmentId}`;
      return await authenticatedApi.delete(url);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid assignment ID', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to delete this individual assignment', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Individual assignment not found', 404);
      }
      throw new ApiError('Failed to delete individual assignment', error.status || 500);
    }
  },

  publishIndividualAssignmentToInProgress: async (assignmentId) => {
    try {
      const url = `${API_BASE_URL}/individual-assignments/${assignmentId}/publish-to-in-progress`;
      return await authenticatedApi.post(url);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.message || 'Invalid assignment ID or assignment cannot be published', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.message || 'You are not authorized to publish this individual assignment', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'Individual assignment not found', 404);
      }
      throw new ApiError('Failed to publish individual assignment to in progress', error.status || 500);
    }
  },

  getEmployeesIndividualAssignments: async () => {
    try {
      const url = `${API_BASE_URL}/feedback-reports/employees-individual-assignments`;
      return await authenticatedApi.get(url);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError(error.data?.message || 'You are not authorized to access individual assignments', 403);
      }
      throw new ApiError('Failed to fetch employees individual assignments', error.status || 500);
    }
  },

  updateIndividualAssignment: async (assignmentData) => {
    try {
      const url = `${API_BASE_URL}/individual-assignments`;
      return await authenticatedApi.put(url, assignmentData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.message || 'Invalid assignment data', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.message || 'You are not authorized to update this individual assignment', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'Individual assignment not found', 404);
      }
      throw new ApiError('Failed to update individual assignment', error.status || 500);
    }
  },

  // ADR-193: what deleting this session would destroy.
  getSessionDeletionPreview: async (sessionId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/feedback-sessions/${sessionId}/deletion-preview`);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError(error.data?.message || 'You are not authorized to delete this feedback session', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'Feedback session not found', 404);
      }
      throw new ApiError(error.data?.message || 'Failed to load what will be deleted', error.status || 500);
    }
  },

  // ADR-192 §12 / ADR-193: `reason` and the preview's `confirmedCounts` are
  // required by the API (400 without them; 409 DELETION_PREVIEW_STALE when the
  // session changed after the preview — `error.data` carries the code).
  deleteSession: async (sessionId, reason, confirmedCounts) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/feedback-sessions/${sessionId}`, { reason, confirmedCounts });
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.message || 'A reason is required to delete a feedback session', 400);
      }
      if (error.status === 409) {
        throw new ApiError(
          error.data?.message || 'This session changed since the preview was shown. Review and confirm again.',
          409,
          error.data
        );
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.message || 'You are not authorized to delete this feedback session', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'Feedback session not found', 404);
      }
      throw new ApiError('Failed to delete feedback session', error.status || 500);
    }
  },

  scheduleSession: async (sessionData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/feedback-sessions/schedule`, sessionData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid session data', 400);
      }
      throw new ApiError('Failed to schedule feedback session', error.status || 500);
    }
  },

  scheduleIndividualAssignments: async (assignmentData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/feedback-sessions/schedule-individual-assignments`, assignmentData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid assignment data', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to schedule individual assignments for some employees', 403);
      }
      throw new ApiError('Failed to schedule individual assignment', error.status || 500);
    }
  },

  updateSession: async (sessionId, sessionData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/feedback-sessions/${sessionId}`, sessionData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid session data', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.message || 'You are not authorized to update this session', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'Feedback session not found', 404);
      }
      throw new ApiError('Failed to update feedback session', error.status || 500);
    }
  },

  updateFeedbackSessionMyAssignments: async (sessionId, employeeAssignments) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/feedback-sessions/${sessionId}/my-assignments`, {
        employeeAssignments
      });
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid assignment data', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to update this session', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Feedback session not found', 404);
      }
      throw new ApiError('Failed to update feedback session assignments', error.status || 500);
    }
  },

  giveFeedback: async (data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/feedback/give`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid feedback request', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Employee not found', 404);
      }
      throw new ApiError('Failed to give feedback', error.status || 500);
    }
  },

  getFeedback: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.type) queryParams.append('type', params.type);
      
      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/feedback${queryString ? `?${queryString}` : ''}`;
      
      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch feedback', error.status || 500);
    }
  },

  acceptFeedback: async (feedbackId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/feedback/${feedbackId}/accept`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid feedback ID', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to accept this feedback', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Feedback not found', 404);
      }
      throw new ApiError('Failed to accept feedback', error.status || 500);
    }
  },

  rejectFeedback: async (feedbackId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/feedback/${feedbackId}/reject`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid feedback ID', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to reject this feedback', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Feedback not found', 404);
      }
      throw new ApiError('Failed to reject feedback', error.status || 500);
    }
  },

  startFeedback: async (feedbackId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/feedback/${feedbackId}/start`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid feedback ID', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to start this feedback', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Feedback not found', 404);
      }
      throw new ApiError('Failed to start feedback', error.status || 500);
    }
  },

  provideFeedback: async (feedbackId, data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/feedback/${feedbackId}/provide`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid feedback data', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to provide this feedback', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Feedback not found', 404);
      }
      throw new ApiError('Failed to provide feedback', error.status || 500);
    }
  },

  submitFinalFeedbackActions: async (assignmentId, data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/feedback/${assignmentId}/session-actions/provide`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid feedback data', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to provide this feedback', 403);
      }
      throw new ApiError('Failed to submit final feedback actions', error.status || 500);
    }
  },

  publishToConfiguration: async (sessionId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/feedback-sessions/${sessionId}/publish-to-configuration`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid session ID', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Feedback session not found', 404);
      }
      throw new ApiError('Failed to publish session to configured state', error.status || 500);
    }
  },

  publishToInProgress: async (sessionId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/feedback-sessions/${sessionId}/publish-to-in-progress`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid session ID', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Feedback session not found', 404);
      }
      throw new ApiError('Failed to publish session to in progress state', error.status || 500);
    }
  },

  publishToCompleted: async (sessionId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/feedback-sessions/${sessionId}/publish-to-completed`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid session ID', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Feedback session not found', 404);
      }
      throw new ApiError('Failed to publish session to completed state', error.status || 500);
    }
  },

  // ADR-014: snapshot close ("close with unfinished work") — terminates a session that still
  // has incomplete assignments, freezing the remaining work exactly as-is.
  // Requires a non-empty reason; the API is gated by the FeedbackSessionClosure
  // permission and the action is irreversible.
  closeWithIncomplete: async (sessionId, reason) => {
    try {
      return await authenticatedApi.post(
        `${API_BASE_URL}/feedback-sessions/${sessionId}/close-with-incomplete`,
        { reason }
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid close request', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to close a session with unfinished work', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Feedback session not found', 404);
      }
      if (error.status === 409) {
        throw new ApiError(error.data?.error || 'Feedback session is already completed', 409);
      }
      throw new ApiError('Failed to close feedback session', error.status || 500);
    }
  },

  getAssignmentReport: async (assignmentId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/feedback-reports/assignments/${assignmentId}`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid assignment', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.message || 'You are not authorized to access this feedback report', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'Feedback assignment not found', 404);
      }
      throw new ApiError('Failed to fetch feedback assignment report', error.status || 500);
    }
  },

  getUserAssignments: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/feedback-reports/user-assignments`);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError(error.data?.message || 'You are not authorized to access your feedback assignments', 403);
      }
      throw new ApiError('Failed to fetch user feedback assignments', error.status || 500);
    }
  },

  getUserConfigurationAssignments: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/feedback-reports/user-configuration-assignments`);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError(error.data?.message || 'You are not authorized to access your configuration assignments', 403);
      }
      throw new ApiError('Failed to fetch user configuration assignments', error.status || 500);
    }
  },

  getEmployeesAssignments: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.cursor) {
        queryParams.append('cursor', params.cursor);
      }
      if (params.limit) {
        queryParams.append('limit', params.limit);
      }
      if (params.orderBy) {
        queryParams.append('orderBy', params.orderBy);
      }
      if (params.orderDirection) {
        queryParams.append('orderDirection', params.orderDirection);
      }
      if (params.feedbackSessionId) {
        queryParams.append('feedbackSessionId', params.feedbackSessionId);
      }
      if (params.sessionStatus) {
        queryParams.append('sessionStatus', params.sessionStatus);
      }
      if (params.reviewType) {
        queryParams.append('reviewType', params.reviewType);
      }
      if (params.employeeId) {
        queryParams.append('employeeId', params.employeeId);
      }
      if (params.completedOnly) {
        queryParams.append('completedOnly', params.completedOnly);
      }
      if (params.leftIncompleteOnly) {
        queryParams.append('leftIncompleteOnly', params.leftIncompleteOnly);
      }
      if (params.assignmentCategory) {
        queryParams.append('assignmentCategory', params.assignmentCategory);
      }
      if (params.assignmentStatus) {
        queryParams.append('assignmentStatus', params.assignmentStatus);
      }

      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/feedback-reports/employees-assignments${queryString ? `?${queryString}` : ''}`;
      return await authenticatedApi.get(url);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError(error.data?.message || 'You are not authorized to access feedback assignments', 403);
      }
      throw new ApiError('Failed to fetch feedback assignments', error.status || 500);
    }
  },

  getMyAssignmentsPendingActions: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/feedback-reports/my-assignments-pending-actions`);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError(error.data?.message || 'You are not authorized to access your pending feedback actions', 403);
      }
      throw new ApiError('Failed to fetch your assignments pending actions', error.status || 500);
    }
  },

  // ===========================================================================
  // ADR-003: session-scoped in-progress modifications (Decisions 1, 6) + the
  // left-employees dashboard query (Action Item 11). Phase-1: admin-only.
  // ===========================================================================

  // Decision 1 — remove a left employee's assignment (hard cascade).
  removeAssignment: async (sessionId, assignmentId, reason) => {
    try {
      const url = `${API_BASE_URL}/feedback-sessions/${sessionId}/assignments/${assignmentId}`;
      return await authenticatedApi.delete(url, { reason });
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid remove-assignment request', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'Admin role required', 403);
      }
      if (error.status === 409) {
        throw new ApiError(error.data?.error || 'Assignment cannot be removed', 409);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Assignment not found', 404);
      }
      throw new ApiError(error.data?.error || 'Failed to remove assignment', error.status || 500);
    }
  },

  // Decision 6 — add an assignee to an open session.
  addAssignee: async (sessionId, data) => {
    try {
      const url = `${API_BASE_URL}/feedback-sessions/${sessionId}/assignments`;
      return await authenticatedApi.post(url, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid add-assignee request', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'Admin role required', 403, error.data);
      }
      if (error.status === 409) {
        throw new ApiError(error.data?.error || 'Assignee cannot be added', 409);
      }
      throw new ApiError(error.data?.error || 'Failed to add assignee', error.status || 500);
    }
  },

  // Action Item 11 — sessions with left employees (Decision-1 cleanup candidates).
  getSessionsWithLeftEmployees: async () => {
    try {
      const url = `${API_BASE_URL}/feedback-sessions/with-left-employees`;
      return await authenticatedApi.get(url);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'Admin role required', 403);
      }
      throw new ApiError('Failed to fetch sessions with left employees', error.status || 500);
    }
  },

};